import {
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import useFormatMessage from '../../../../hooks/useFormatMessage';
import moment from 'moment';
import {
  EXCLUDED_REPORTS_MVP,
  FREQUENCY_OPTIONS,
  REPORTING_PERIOD_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  DAY_OF_MONTH_OPTIONS,
  AM_PM_OPTIONS,
  TIME_OPTIONS,
  FORMAT_OPTIONS,
  DEFAULT_TIMEZONE,
  LEASE_TYPE_OPTIONS,
  LEASE_CHANGE_OUT_REF_ID,
} from './constants';
import {
  rangeIncludesDayOfWeek,
  rangeIncludesDayOfMonth,
  rangeAllowsAtLeastOneCycle,
  rangeAllowsAnyExecutionFromNow,
} from './dateRangeValidation';
import { convert12hTo24h } from './timeFormat';
import messages from './messages';

/**
 * Default reporting period when no report is selected.
 */
const DEFAULT_REPORTING_PERIOD = 'asOfDate';

/**
 * Get the reporting period for a given report based on report metadata.
 *
 * For legacy reports: uses runByDate/runByPeriod fields from backend
 * For new reports: uses timeSelectionOptions from configuration
 *
 * @param {Object} report - The selected report object
 * @returns {'asOfDate' | 'periodToDate'}
 */
const getReportingPeriodForReport = (report) => {
  if (!report) return DEFAULT_REPORTING_PERIOD;

  // Legacy reports: use runByDate field (reports with date filter use period range)
  if (report.version === 'legacy') {
    // runByDate indicates the report uses a date range filter
    if (report.runByDate || report.runByPeriod) return 'periodToDate';
    return 'asOfDate';
  }

  // New reports: use timeSelectionOptions from configuration
  const timeOptions = report.configuration?.timeSelectionOptions || [];
  if (timeOptions.includes('RANGE')) return 'periodToDate';
  return 'asOfDate';
};

const CreateScheduledReportForm = forwardRef(
  (
    {
      organizationId,
      configuration,
      legacyFormatFields = {},
      userProperties = [],
      userPropertyGroups = [],
      availableReports = [],
      onValidationChange,
      onSubmit,
      onReportChange,
      isEditMode = false,
      initialValues = null,
      reportName = null,
      scheduleId = null,
      onDuplicateEmailChange,
    },
    ref,
  ) => {
    const formatMessage = useFormatMessage();

    // Transform user properties to options format
    const propertyOptions = useMemo(() => {
      if (!userProperties || !Array.isArray(userProperties)) {
        return [];
      }

      const activeProperties = userProperties.filter(
        (prop) => prop?.propertyStatus?.description !== 'Inactive',
      );

      return activeProperties.map((prop) => ({
        text: prop.name || prop.id,
        value: prop.id,
        disabled: false,
      }));
    }, [userProperties]);

    // Transform user property groups to options format
    const propertyGroupOptions = useMemo(() => {
      if (!userPropertyGroups || !Array.isArray(userPropertyGroups)) {
        return [];
      }

      return userPropertyGroups.map((group) => ({
        text: group.name || group.id,
        value: group.id,
        disabled: false,
      }));
    }, [userPropertyGroups]);

    // Transform available reports to options format
    // Filter: exclude only MVP-excluded reports
    // Add "(Portfolio)" prefix for reports where isPropertyLevel === false
    const reportOptions = useMemo(() => {
      if (!availableReports || !Array.isArray(availableReports)) {
        return [];
      }

      return availableReports
        .filter((report) => {
          // Exclude MVP-excluded reports
          if (EXCLUDED_REPORTS_MVP.includes(report.referenceId)) {
            return false;
          }
          return true;
        })
        .map((report) => {
          const reportName = report.name || report.id;
          const displayName =
            report.isPropertyLevel === false
              ? `(Portfolio) ${reportName}`
              : reportName;
          return {
            text: displayName,
            value: report.id,
            disabled: false,
          };
        });
    }, [availableReports]);

    // Format options based on configuration or legacy fields
    const formatOptions = useMemo(() => {
      // For "new" reports: use configuration.formats
      if (configuration?.formats && configuration.formats.length > 0) {
        return configuration.formats.map((fmt) => {
          const formatUpper = fmt.toUpperCase();
          if (formatUpper === 'CSV') {
            return { text: 'CSV', value: 'csv' };
          } else if (formatUpper === 'EXCEL') {
            return { text: 'Excel', value: 'xlsx' };
          } else if (formatUpper === 'PDF') {
            return { text: 'PDF', value: 'pdf' };
          }
          return { text: fmt, value: fmt.toLowerCase() };
        });
      }

      // For "legacy" reports: use monthendCSV and monthendPDF fields
      const { monthendCSV, monthendPDF } = legacyFormatFields;
      if (monthendCSV !== undefined || monthendPDF !== undefined) {
        const options = [];
        if (monthendCSV) {
          options.push({ text: 'CSV', value: 'csv' });
        }
        if (monthendPDF) {
          options.push({ text: 'PDF', value: 'pdf' });
        }
        // If no formats enabled, default to CSV
        if (options.length === 0) {
          options.push({ text: 'CSV', value: 'csv' });
        }
        return options;
      }

      // Fallback for reports without any format configuration
      return FORMAT_OPTIONS;
    }, [configuration?.formats, legacyFormatFields]);

    // Compute default values based on mode
    const defaultFormValues = useMemo(() => {
      if (isEditMode && initialValues) {
        return {
          report: initialValues.report || '',
          propertyGroup: initialValues.propertyGroup || '',
          properties: initialValues.properties || [],
          format: initialValues.format || '',
          reportingPeriod: initialValues.reportingPeriod || 'asOfDate',
          frequency: initialValues.frequency || 'daily',
          dayOfWeek: initialValues.dayOfWeek,
          dayOfMonth: initialValues.dayOfMonth,
          executionTime: initialValues.executionTime || '12:00',
          amPm: initialValues.amPm || 'AM',
          executionDate: initialValues.executionDate || null,
          startDate: initialValues.startDate || moment().format('MM/DD/YYYY'),
          endDate: initialValues.endDate || null,
          targetEmails: initialValues.targetEmails || [],
          leaseType: initialValues.leaseType || '',
        };
      }

      return {
        report: '',
        propertyGroup: '',
        properties: [],
        format: '',
        reportingPeriod: 'asOfDate', // Default, will be auto-set based on report selection
        frequency: 'daily',
        dayOfWeek: undefined,
        dayOfMonth: undefined,
        executionTime: '12:00',
        amPm: 'AM',
        startDate: moment().format('MM/DD/YYYY'),
        endDate: null,
        targetEmails: [],
        leaseType: '',
      };
    }, [isEditMode, initialValues]);

    const ReactHookFormProps = useReactHookFormProps({
      name: 'createScheduledReportForm',
      defaultValues: defaultFormValues,
      mode: 'onChange',
    });

    // Reset form when entering edit mode with initial values
    // scheduleId is used as a stable identifier to trigger reset when editing a different schedule
    const resetForm = ReactHookFormProps?.reset;
    const triggerAfterReset = ReactHookFormProps?.trigger;
    useEffect(() => {
      if (resetForm && isEditMode && scheduleId) {
        resetForm(defaultFormValues);
        // After reset, run cross-field validation so any inconsistency between
        // saved startDate/endDate and dayOfWeek/dayOfMonth surfaces inline.
        if (triggerAfterReset) {
          triggerAfterReset('endDate');
        }
      }
    }, [
      isEditMode,
      scheduleId,
      resetForm,
      defaultFormValues,
      triggerAfterReset,
    ]);

    const selectedPropertyGroupId = ReactHookFormProps?.watch
      ? ReactHookFormProps.watch('propertyGroup')
      : null;

    // In edit mode the Report field is hidden and its watched value can be
    // transiently cleared by the reset effect, so read the id from the stable
    // initialValues prop instead.
    const selectedReportId = isEditMode
      ? initialValues?.report
      : ReactHookFormProps?.watch
      ? ReactHookFormProps.watch('report')
      : null;

    const setValue = ReactHookFormProps?.setValue;
    const getValues = ReactHookFormProps?.getValues;

    // Get selected report data for determining reporting period
    const selectedReportData = useMemo(() => {
      if (!availableReports || !Array.isArray(availableReports)) {
        return null;
      }
      return (
        availableReports.find((report) => report.id === selectedReportId) ||
        // Legacy schedules store a Postgres reportId that does not match the
        // Mongo definition id, so fall back to matching by name in edit mode.
        (isEditMode && reportName
          ? availableReports.find((report) => report.name === reportName)
          : null) ||
        null
      );
    }, [availableReports, selectedReportId, isEditMode, reportName]);

    const isLeaseChangeOut =
      selectedReportData?.referenceId === LEASE_CHANGE_OUT_REF_ID;

    // Determine reporting period based on report metadata
    // (runByDate/runByPeriod for legacy, timeSelectionOptions for new reports)
    const reportingPeriodFromMapping = useMemo(() => {
      return getReportingPeriodForReport(selectedReportData);
    }, [selectedReportData]);

    // Per requirements: Reporting Period field is always visible and always disabled (read-only)
    // The value is auto-selected based on the report when one is chosen

    // Notify parent when report changes
    useEffect(() => {
      if (onReportChange && selectedReportId) {
        const selectedReport = availableReports.find(
          (report) => report.id === selectedReportId,
        );
        if (selectedReport) {
          onReportChange(selectedReport);
        }
      }
    }, [selectedReportId, availableReports, onReportChange]);

    // Auto-set reporting period based on selected report's mapping
    // This ensures the correct period is always set when a report is selected,
    // but preserves the existing value in edit mode to avoid overwriting saved data
    useEffect(() => {
      if (!setValue || !reportingPeriodFromMapping) return;

      // In edit mode, preserve the existing reportingPeriod from initialValues
      const currentReportingPeriod =
        ReactHookFormProps?.getValues?.('reportingPeriod');
      if (isEditMode && currentReportingPeriod) {
        return;
      }

      setValue('reportingPeriod', reportingPeriodFromMapping);
    }, [reportingPeriodFromMapping, setValue, isEditMode, ReactHookFormProps]);

    // Clear leaseType when the selected report is not Lease Change Out, so a
    // stale value never leaks when the user switches away from that report.
    useEffect(() => {
      if (!setValue) return;
      if (!isLeaseChangeOut) setValue('leaseType', '');
    }, [isLeaseChangeOut, setValue]);

    // Auto-set format based on configuration
    // If 1 format available: use it; If 2 formats (pdf+csv): default to csv
    useEffect(() => {
      if (!setValue || !formatOptions || formatOptions.length === 0) return;

      // Only set default if format is empty
      const currentFormat = ReactHookFormProps?.getValues?.('format');
      if (currentFormat) return;

      if (formatOptions.length === 1) {
        setValue('format', formatOptions[0].value);
      } else {
        // If multiple formats, default to CSV if available
        const csvOption = formatOptions.find(
          (opt) => opt.value === 'csv' || opt.value === 'CSV',
        );
        if (csvOption) {
          setValue('format', csvOption.value);
        } else {
          setValue('format', formatOptions[0].value);
        }
      }
    }, [formatOptions, setValue, ReactHookFormProps]);

    // Auto-fill properties when property group is selected
    useEffect(() => {
      if (!selectedPropertyGroupId || !setValue) return;

      const selectedGroup = userPropertyGroups.find(
        (group) => group.id === selectedPropertyGroupId,
      );

      if (selectedGroup && selectedGroup.propertyIds) {
        const availablePropertyIds = propertyOptions.map((opt) => opt.value);
        const propertyIdsToSelect = selectedGroup.propertyIds.filter((id) =>
          availablePropertyIds.includes(id),
        );

        if (propertyIdsToSelect.length > 0) {
          setValue('properties', propertyIdsToSelect);
        }
      }
    }, [
      selectedPropertyGroupId,
      userPropertyGroups,
      propertyOptions,
      setValue,
    ]);

    const handleFormSubmit = useCallback(
      (formValues) => {
        // Note: 12h to 24h conversion is done in parent component (index.js)
        const payload = {
          ...formValues,
          timezone: DEFAULT_TIMEZONE,
        };

        if (onSubmit) {
          onSubmit(payload);
        }
      },
      [onSubmit],
    );

    // Expose submit method via ref
    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (ReactHookFormProps?.handleSubmit) {
            ReactHookFormProps.handleSubmit(handleFormSubmit)();
          }
        },
      }),
      [ReactHookFormProps, handleFormSubmit],
    );

    // Track form validity and notify parent
    const formIsValid = ReactHookFormProps?.isValid;
    useEffect(() => {
      if (onValidationChange) {
        onValidationChange(formIsValid);
      }
    }, [onValidationChange, formIsValid]);

    // Watch for date changes and trigger cross-field validation
    const watchedStartDate = ReactHookFormProps?.watch?.('startDate');
    const watchedEndDate = ReactHookFormProps?.watch?.('endDate');
    const watchedDayOfWeek = ReactHookFormProps?.watch?.('dayOfWeek');
    const watchedDayOfMonth = ReactHookFormProps?.watch?.('dayOfMonth');
    const watchedFrequency = ReactHookFormProps?.watch?.('frequency');
    const watchedExecutionTime = ReactHookFormProps?.watch?.('executionTime');
    const watchedAmPm = ReactHookFormProps?.watch?.('amPm');
    const trigger = ReactHookFormProps?.trigger;

    // Use refs to track if this is the initial mount
    const isInitialStartDate = useRef(true);
    const isInitialEndDate = useRef(true);
    const isInitialDayOfWeek = useRef(true);
    const isInitialDayOfMonth = useRef(true);
    const isInitialFrequency = useRef(true);
    const isInitialExecutionTime = useRef(true);
    const isInitialAmPm = useRef(true);

    useEffect(() => {
      // Skip validation trigger on initial mount
      if (isInitialStartDate.current) {
        isInitialStartDate.current = false;
        return;
      }
      if (trigger && watchedStartDate) {
        trigger('endDate');
      }
    }, [watchedStartDate, trigger]);

    useEffect(() => {
      // Skip validation trigger on initial mount
      if (isInitialEndDate.current) {
        isInitialEndDate.current = false;
        return;
      }
      if (trigger && watchedEndDate) {
        trigger('startDate');
      }
    }, [watchedEndDate, trigger]);

    // Trigger endDate revalidation when dayOfWeek / dayOfMonth / frequency
    // change so the cross-field rule (range must include weekday / day-of-month)
    // updates inline instead of waiting for the user to touch the date again.
    useEffect(() => {
      if (isInitialDayOfWeek.current) {
        isInitialDayOfWeek.current = false;
        return;
      }
      if (trigger) {
        trigger('endDate');
      }
    }, [watchedDayOfWeek, trigger]);

    useEffect(() => {
      if (isInitialDayOfMonth.current) {
        isInitialDayOfMonth.current = false;
        return;
      }
      if (trigger) {
        trigger('endDate');
      }
    }, [watchedDayOfMonth, trigger]);

    useEffect(() => {
      if (isInitialFrequency.current) {
        isInitialFrequency.current = false;
        return;
      }
      if (trigger) {
        trigger('endDate');
      }
    }, [watchedFrequency, trigger]);

    // Trigger endDate revalidation when the execution time (hours or AM/PM)
    // changes so the daily window check (ENG-27666 T2) updates inline.
    useEffect(() => {
      if (isInitialExecutionTime.current) {
        isInitialExecutionTime.current = false;
        return;
      }
      if (trigger) {
        trigger('endDate');
      }
    }, [watchedExecutionTime, trigger]);

    useEffect(() => {
      if (isInitialAmPm.current) {
        isInitialAmPm.current = false;
        return;
      }
      if (trigger) {
        trigger('endDate');
      }
    }, [watchedAmPm, trigger]);

    const fields = useMemo(() => {
      const fieldsArray = [];

      // Report selector (required) - hidden in edit mode
      fieldsArray.push({
        name: 'report',
        label: 'Report',
        variant: 'singleSelect',
        options: reportOptions,
        placeholder: 'Name of Report',
        hidden: isEditMode,
        rules: {
          required: isEditMode ? false : 'Report is required',
        },
        GridProps: {
          xs: 12,
        },
      });

      // Report name display (read-only in edit mode)
      if (isEditMode && reportName) {
        fieldsArray.push({
          name: 'reportNameDisplay',
          variant: 'custom',
          component: (
            <>
              <Typography variant="labelSmall" color="text.secondary">
                Report
              </Typography>
              <Typography variant="bodyMedium" component="div">
                {reportName}
              </Typography>
            </>
          ),
          GridProps: {
            xs: 12,
          },
        });
      }

      // Properties Section Divider
      fieldsArray.push({
        name: 'propertiesSectionDivider',
        variant: 'custom',
        component: (
          <Typography variant="titleSmall" color="text.secondary">
            Properties and format
          </Typography>
        ),
        GridProps: {
          xs: 12,
        },
      });

      // Property Group (optional, always shown but disabled when no groups available)
      const hasPropertyGroups = propertyGroupOptions.length > 0;
      fieldsArray.push({
        name: 'propertyGroup',
        label: 'Property Group',
        variant: 'singleSelect',
        options: propertyGroupOptions,
        placeholder: hasPropertyGroups
          ? 'Select property group...'
          : 'No property groups available',
        disabled: !hasPropertyGroups,
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // Properties (required - multiSelect)
      fieldsArray.push({
        name: 'properties',
        label: 'Properties',
        variant: 'multiSelect',
        options: propertyOptions,
        placeholder: 'Select properties...',
        selectAllOption: true,
        rules: {
          required: 'At least one property is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // Lease Type (required only for the Lease Change Out report) (ENG-27547)
      fieldsArray.push({
        name: 'leaseType',
        label: 'Lease Type',
        variant: 'singleSelect',
        options: LEASE_TYPE_OPTIONS,
        placeholder: 'Select lease type...',
        hidden: !isLeaseChangeOut,
        rules: {
          required: isLeaseChangeOut ? 'Lease Type is required' : false,
        },
        GridProps: { xs: 12, sm: 6 },
      });

      // Format
      fieldsArray.push({
        name: 'format',
        label: 'Format',
        variant: 'singleSelect',
        options: formatOptions,
        placeholder: 'Select format...',
        rules: {
          required: 'Format is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // Reporting Period - always visible and always disabled (read-only)
      // System auto-selects based on report type (asOfDate or periodToDate)
      fieldsArray.push({
        name: 'reportingPeriod',
        label: 'Reporting Period',
        variant: 'radioGroup',
        options: REPORTING_PERIOD_OPTIONS,
        row: true,
        disabled: true, // Always disabled - auto-selected based on report
        rules: {
          required: true,
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // Frequency
      fieldsArray.push({
        name: 'frequency',
        label: 'Frequency',
        variant: 'radioGroup',
        options: FREQUENCY_OPTIONS,
        row: true,
        rules: {
          required: 'Frequency is required',
        },
        GridProps: {
          xs: 12,
          sx: { marginTop: 2 },
        },
      });

      // Day of Week (for weekly frequency)
      fieldsArray.push({
        name: 'dayOfWeek',
        label: 'Day of Week',
        variant: 'singleSelect',
        options: DAY_OF_WEEK_OPTIONS,
        placeholder: 'Select day...',
        hidden: ({ getValues }) => getValues('frequency') !== 'weekly',
        watchFields: ['frequency'],
        rules: {
          required: 'Day of week is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });
      // Spacer to force line break after Day of Week
      fieldsArray.push({
        name: 'dayOfWeekSpacer',
        variant: 'custom',
        component: null,
        hidden: ({ getValues }) => getValues('frequency') !== 'weekly',
        watchFields: ['frequency'],
        GridProps: {
          xs: false,
          sm: 6,
        },
      });

      // Day of Month (for monthly frequency)
      fieldsArray.push({
        name: 'dayOfMonth',
        label: 'Day of Month',
        variant: 'singleSelect',
        options: DAY_OF_MONTH_OPTIONS,
        placeholder: 'Select day...',
        hidden: ({ getValues }) => getValues('frequency') !== 'monthly',
        watchFields: ['frequency'],
        rules: {
          required: 'Day of month is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });
      // Spacer to force line break after Day of Month
      fieldsArray.push({
        name: 'dayOfMonthSpacer',
        variant: 'custom',
        component: null,
        hidden: ({ getValues }) => getValues('frequency') !== 'monthly',
        watchFields: ['frequency'],
        GridProps: {
          xs: false,
          sm: 6,
        },
      });

      // Date field for one-time frequency (before Time)
      fieldsArray.push({
        name: 'executionDate',
        label: 'Date',
        variant: 'date',
        placeholder: 'MM/DD/YYYY',
        minDate: moment().startOf('day'),
        hidden: ({ getValues }) => getValues('frequency') !== 'one-time',
        watchFields: ['frequency'],
        rules: {
          required: 'Date is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });
      // Spacer to force line break after Date
      fieldsArray.push({
        name: 'dateSpacer',
        variant: 'custom',
        component: null,
        hidden: ({ getValues }) => getValues('frequency') !== 'one-time',
        watchFields: ['frequency'],
        GridProps: {
          xs: false,
          sm: 6,
        },
      });

      // Time
      fieldsArray.push({
        name: 'executionTime',
        label: 'Time',
        variant: 'singleSelect',
        options: TIME_OPTIONS,
        placeholder: 'Select time...',
        rules: {
          required: 'Time is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // AM/PM
      fieldsArray.push({
        name: 'amPm',
        label: 'AM/PM',
        variant: 'singleSelect',
        options: AM_PM_OPTIONS,
        rules: {
          required: 'AM/PM is required',
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // Start Date (for daily, weekly, monthly - not one-time)
      fieldsArray.push({
        name: 'startDate',
        label: 'Start Date',
        variant: 'date',
        placeholder: 'MM/DD/YYYY',
        minDate: moment().startOf('day'),
        hidden: ({ getValues }) => getValues('frequency') === 'one-time',
        watchFields: ['frequency', 'endDate'],
        rules: {
          required: 'Start date is required',
          validate: (value) => {
            const currentEndDate = getValues?.('endDate');
            if (!value || !currentEndDate) {
              return true;
            }
            const startDate = moment(value, 'MM/DD/YYYY');
            const endDate = moment(currentEndDate, 'MM/DD/YYYY');
            if (startDate.isAfter(endDate)) {
              return 'Start date must be on or before end date';
            }
            return true;
          },
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // End Date (for daily, weekly, monthly - not one-time)
      fieldsArray.push({
        name: 'endDate',
        label: 'End Date',
        variant: 'date',
        placeholder: 'MM/DD/YYYY',
        minDate: moment().startOf('day'),
        hidden: ({ getValues }) => getValues('frequency') === 'one-time',
        watchFields: [
          'frequency',
          'startDate',
          'dayOfWeek',
          'dayOfMonth',
          'executionTime',
          'amPm',
        ],
        rules: {
          validate: (value) => {
            const currentStartDate = getValues?.('startDate');
            const currentFrequency = getValues?.('frequency');
            const currentDayOfWeek = getValues?.('dayOfWeek');
            const currentDayOfMonth = getValues?.('dayOfMonth');
            const currentExecutionTime = getValues?.('executionTime');
            const currentAmPm = getValues?.('amPm');

            if (!value || !currentStartDate) {
              return true;
            }

            // "End >= Start" rule. Runs ALWAYS, in both create and edit
            // mode. This is the only End Date check that survives in edit
            // mode, because an endDate before the startDate is never a
            // valid window for any backend behavior.
            const startDate = moment(currentStartDate, 'MM/DD/YYYY');
            const endDate = moment(value, 'MM/DD/YYYY');
            if (endDate.isBefore(startDate)) {
              return 'End date must be on or after start date';
            }

            // Window validators below run ONLY in create mode.
            //
            // ENG-27666 followup (Kelsey feedback): in edit mode the user
            // cannot delete a scheduled report, so setting any endDate is
            // the only way to stop a schedule that was created by mistake,
            // even when no future execution is left in the window. The
            // backend now pre-validates the window and performs a graceful
            // stop without an error for any endDate, so these four window
            // checks (rangeIncludesDayOfWeek, rangeIncludesDayOfMonth,
            // rangeAllowsAtLeastOneCycle and the rangeAllowsAnyExecutionFromNow
            // catchall) became an unnecessary restriction in edit mode and
            // are skipped entirely when isEditMode is true. Create mode
            // keeps all of them, since a brand new schedule should be
            // authored with a window that actually produces executions.
            if (!isEditMode) {
              // Weekly: range must contain the chosen weekday (avoids
              // EventBridge error). Helper short-circuits to true on
              // missing/invalid inputs, so the outer guards we used to have
              // here are redundant.
              if (
                currentFrequency === 'weekly' &&
                !rangeIncludesDayOfWeek(
                  currentStartDate,
                  value,
                  currentDayOfWeek,
                )
              ) {
                const weekdayLabel = DAY_OF_WEEK_OPTIONS.find(
                  (opt) => String(opt.value) === String(currentDayOfWeek),
                )?.text;
                return formatMessage(messages.rangeMustIncludeWeekday, {
                  weekday: weekdayLabel,
                });
              }

              // Monthly: range must contain the chosen day-of-month (or
              // last day). Same as above: helper handles missing/invalid
              // inputs internally.
              if (
                currentFrequency === 'monthly' &&
                !rangeIncludesDayOfMonth(
                  currentStartDate,
                  value,
                  currentDayOfMonth,
                )
              ) {
                return formatMessage(messages.rangeMustIncludeDayOfMonth, {
                  kind: String(currentDayOfMonth) === '-1' ? 'last' : 'day',
                  dayOfMonth: currentDayOfMonth,
                });
              }

              // Recurring schedules: range must allow at least one complete
              // cycle. AWS EventBridge requires StartDate strictly before
              // EndDate.
              if (
                (currentFrequency === 'weekly' ||
                  currentFrequency === 'monthly') &&
                !rangeAllowsAtLeastOneCycle(currentStartDate, value)
              ) {
                return formatMessage(messages.rangeMustAllowOneCycle);
              }

              // Recurring catchall (ENG-27666 T2 and weekly/monthly
              // extensions): the helper computes the next execution
              // datetime from the first execution and returns false when
              // that datetime falls outside the [start, end] window. This
              // catches the daily "endDate=today, time already passed"
              // scenario AND the weekly/monthly equivalents (e.g. the only
              // matching weekday or day of month inside the window is
              // "today" but the executionTime already passed). Kept LAST so
              // the more specific validators above (rangeIncludesDayOfWeek,
              // rangeIncludesDayOfMonth, rangeAllowsAtLeastOneCycle) win
              // when applicable, since their messages are more actionable
              // than this catchall.
              if (
                currentFrequency === 'daily' ||
                currentFrequency === 'weekly' ||
                currentFrequency === 'monthly'
              ) {
                const executionTime24h = convert12hTo24h(
                  currentExecutionTime,
                  currentAmPm,
                );
                if (
                  executionTime24h &&
                  !rangeAllowsAnyExecutionFromNow({
                    frequency: currentFrequency,
                    startDate: currentStartDate,
                    endDate: value,
                    executionTime24h,
                    dayOfWeek: currentDayOfWeek,
                    dayOfMonth: currentDayOfMonth,
                    isEditMode,
                  })
                ) {
                  return formatMessage(messages.rangeMustAllowAnyExecution);
                }
              }
            }

            return true;
          },
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
      });

      // Delivery Email(s)
      fieldsArray.push({
        name: 'targetEmails',
        variant: 'chips',
        label: 'Delivery Email(s)',
        tooltipText:
          'Enter one or more email addresses to receive the report. ' +
          'Each recipient will get an email with the report attached ' +
          'at the scheduled date and time.',
        validationType: 'email',
        placeholder: 'Enter email address',
        onDuplicateChange: onDuplicateEmailChange,
        rules: {
          required: 'At least one email is required',
        },
        GridProps: {
          xs: 12,
          sx: { marginTop: 2 },
        },
      });

      return fieldsArray;
    }, [
      reportOptions,
      propertyOptions,
      propertyGroupOptions,
      formatOptions,
      isEditMode,
      isLeaseChangeOut,
      reportName,
      getValues,
      onDuplicateEmailChange,
      formatMessage,
    ]);

    return (
      <Form
        fields={fields}
        ReactHookFormProps={ReactHookFormProps}
        onSubmit={handleFormSubmit}
        hideSubmitButton
        containerSX={{
          textAlign: 'left',
          width: '100%',
          maxWidth: '100%',
        }}
      />
    );
  },
);

CreateScheduledReportForm.displayName = 'CreateScheduledReportForm';

export default CreateScheduledReportForm;
